import { Injectable }      from '@angular/core';
import { HttpClient }      from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import {
  CurrentSubscription,
  PlanDefinition,
  PaymentInit
} from '../models/subscription.model';
import { environment } from 'src/app/environments/environment';

@Injectable({ providedIn: 'root' })
export class SubscriptionService {

  private api = `${environment.apiUrl}/api/subscription`;

  private subscription$ =
    new BehaviorSubject<CurrentSubscription | null>(null);

  currentSubscription$ = this.subscription$.asObservable();

  constructor(private http: HttpClient) {}

  loadCurrent(): Observable<CurrentSubscription> {
    return this.http.get<CurrentSubscription>(`${this.api}/current`).pipe(
      tap(sub => this.subscription$.next(sub))
    );
  }

  getCurrent(): CurrentSubscription | null {
    return this.subscription$.getValue();
  }

  getPlans(): Observable<PlanDefinition[]> {
    return this.http.get<PlanDefinition[]>(`${this.api}/plans`);
  }

  // ✅ FIXED — now accepts and sends callbackUrl. Previously this method
  // never sent a callback_url to the backend at all, so Paystack had no
  // reliable way to redirect back to /payment/success after payment.
  initializePayment(
    plan: string,
    billingCycle: string,
    callbackUrl: string
  ): Observable<PaymentInit> {
    return this.http.post<PaymentInit>(
      `${this.api}/initialize`, { plan, billingCycle, callbackUrl });
  }

  verifyPayment(reference: string): Observable<any> {
    return this.http.post(`${this.api}/verify`, { reference });
  }

  getPaymentHistory(): Observable<any[]> {
    return this.http.get<any[]>(`${this.api}/payment-history`);
  }

  // ── Feature access helpers ────────────────────────────

  canAccess(feature: keyof CurrentSubscription['limits']): boolean {
    const sub = this.subscription$.getValue();
    if (!sub) return false;

    // Expired or PastDue subscriptions lose access to all premium features.
    if (sub.status === 'Expired' || sub.status === 'PastDue')
      return false;

    return !!sub.limits[feature];
  }

  isFreePlan(): boolean {
    return this.subscription$.getValue()?.plan === 'Free';
  }

  isExpired(): boolean {
    const sub = this.subscription$.getValue();
    return sub?.status === 'Expired' || sub?.status === 'PastDue';
  }

  isTrialActive(): boolean {
    const sub = this.subscription$.getValue();
    return sub?.isTrial === true && sub?.isTrialValid === true;
  }

  canUseAiInsights(): boolean {
    const sub = this.subscription$.getValue();
    if (!sub) return false;
    if (sub.status === 'Expired' || sub.status === 'PastDue') return false;
    return sub.canUseAiInsights === true;
  }

  getDaysRemaining(): number {
    const sub = this.subscription$.getValue();
    if (!sub) return 0;
    return sub.isTrial ? sub.trialDaysRemaining : sub.daysRemaining;
  }
}